import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import api from "services/api";

const BillingDetails = ({
  setFieldValue,
  values,
  countries,
  setFieldError,
  errors,
  touched,
}) => {
  const [states, setStates] = useState([]);

  // Fetch states when country changes
  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async (countryId) => {
    try {
      const response = await api.get(`/states?country_id=95`);
      setStates(response.data.data.states || []);
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([]);
    }
  };

  const handleCountryChange = (e) => {
    // Since country is fixed to India (95), we don't allow changes
    // but we still need to ensure the value is set
    setFieldValue("billing_country_id", "95");
    setFieldValue("billing_state_name", ""); // Reset state when country changes
  };

  const handleStateChange = (e) => {
    setFieldValue("billing_state_name", e.target.value);
  };

  const handleGSTINChange = (e) => {
    const value = e.target.value.toUpperCase();
    if (/^[0-9A-Z]*$/.test(value) && value.length <= 15) {
      setFieldValue("billing_gstin", value);
    }
  };

  const handlePANChange = (e) => {
    const value = e.target.value.toUpperCase();
    if (/^[A-Z0-9]*$/.test(value) && value.length <= 10) {
      setFieldValue("billing_pan", value);
    }
  };

  return (
    <div className="card mb-4 mx-4">
      <h5 className="card-header">Billing Details</h5>
      <div className="card-body">
        <div className="row mb-3">
          <div className="col-md-6 col-12">
            <label htmlFor="billing_name" className="form-label">
              Name <span className="invalid-form">*</span>
            </label>
            <Field
              type="text"
              className={`form-control ${
                errors.billing_name && touched.billing_name ? "is-invalid" : ""
              }`}
              name="billing_name"
              placeholder="Enter billing name"
              onChange={(e) => {
                if (/^[A-Za-z ]*$/.test(e.target.value)) {
                  setFieldValue("billing_name", e.target.value);
                }
              }}
            />
            <ErrorMessage
              name="billing_name"
              component="div"
              className="invalid-form"
            />
          </div>
          <div className="col-md-6 col-12">
            <label htmlFor="billing_email" className="form-label">
              Email <span className="invalid-form">*</span>
            </label>
            <Field
              type="email"
              className={`form-control ${
                errors.billing_email && touched.billing_email
                  ? "is-invalid"
                  : ""
              }`}
              name="billing_email"
              placeholder="Enter billing email"
              onChange={(e) => {
                setFieldValue("billing_email", e.target.value);
              }}
            />
            <ErrorMessage
              name="billing_email"
              component="div"
              className="invalid-form"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 col-12">
            <label htmlFor="billing_phone" className="form-label">
              Phone Number <span className="invalid-form">*</span>
            </label>
            <Field
              type="text"
              className={`form-control ${
                errors.billing_phone && touched.billing_phone
                  ? "is-invalid"
                  : ""
              }`}
              name="billing_phone"
              placeholder="Enter billing phone number"
              maxLength="10"
              onChange={(e) => {
                if (/^[0-9]*$/.test(e.target.value)) {
                  setFieldValue("billing_phone", e.target.value);
                }
              }}
            />
            <ErrorMessage
              name="billing_phone"
              component="div"
              className="invalid-form"
            />
          </div>
          <div className="col-md-6 col-12">
            <label htmlFor="billing_address" className="form-label">
              Address <span className="invalid-form">*</span>
            </label>
            <Field
              as="textarea"
              className={`form-control ${
                errors.billing_address && touched.billing_address
                  ? "is-invalid"
                  : ""
              }`}
              name="billing_address"
              placeholder="Enter complete billing address"
              rows="3"
              onChange={(e) => {
                setFieldValue("billing_address", e.target.value);
              }}
            />
            <ErrorMessage
              name="billing_address"
              component="div"
              className="invalid-form"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 col-12">
            <label htmlFor="billing_country_id" className="form-label">
              Country <span className="invalid-form">*</span>
            </label>
            <Field
              as="select"
              className={`select2 form-select ${
                errors.billing_country_id && touched.billing_country_id
                  ? "is-invalid"
                  : ""
              }`}
              name="billing_country_id"
              onChange={handleCountryChange}
              value="95"
              disabled={true}
              style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
            >
              <option value="">Select Country</option>
              {countries?.map((country) => (
                <option
                  key={country.id}
                  value={country.id}
                  selected={country.id === 95}
                >
                  {country.name}
                </option>
              ))}
            </Field>
            <ErrorMessage
              name="billing_country_id"
              component="div"
              className="invalid-form"
            />
          </div>
          <div className="col-md-6 col-12">
            <label htmlFor="billing_state_name" className="form-label">
              State <span className="invalid-form">*</span>
            </label>
            <Field
              as="select"
              className={`select2 form-select ${
                errors.billing_state_name && touched.billing_state_name
                  ? "is-invalid"
                  : ""
              }`}
              name="billing_state_name"
              onChange={handleStateChange}
              //  disabled={!selectedCountry}
            >
              <option value="">Select State</option>
              {states?.map((state) => (
                <option key={state.id} value={state.name}>
                  {state.name}
                </option>
              ))}
            </Field>
            <ErrorMessage
              name="billing_state_name"
              component="div"
              className="invalid-form"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 col-12">
            <label htmlFor="billing_gstin" className="form-label">
              GSTIN (Optional)
            </label>
            <Field
              type="text"
              className={`form-control ${
                errors.billing_gstin && touched.billing_gstin
                  ? "is-invalid"
                  : ""
              }`}
              name="billing_gstin"
              placeholder="Enter 15-digit GSTIN"
              maxLength="15"
              onChange={handleGSTINChange}
            />
            <ErrorMessage
              name="billing_gstin"
              component="div"
              className="invalid-form"
            />
            <small className="form-text text-muted">
              Format: 22AAAAA0000A1Z5 (15 characters)
            </small>
          </div>
          <div className="col-md-6 col-12">
            <label htmlFor="billing_pan" className="form-label">
              PAN (Optional)
            </label>
            <Field
              type="text"
              className={`form-control ${
                errors.billing_pan && touched.billing_pan ? "is-invalid" : ""
              }`}
              name="billing_pan"
              placeholder="Enter PAN number"
              maxLength="10"
              onChange={handlePANChange}
            />
            <ErrorMessage
              name="billing_pan"
              component="div"
              className="invalid-form"
            />
            <small className="form-text text-muted">
              Format: ABCDE1234F (10 characters)
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingDetails;
